import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function Login() {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();

  const [userid, setUserid]     = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp]           = useState('');
  const [otpToken, setOtpToken] = useState(null); // non-null = OTP step active
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  // Step 1 — validate credentials, trigger OTP send
  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res  = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userid, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');
      // Credentials valid — move to OTP step
      setOtpToken(data.otpToken);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2 — submit OTP
  const handleVerify = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res  = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ otpToken, otp }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Verification failed');
      login(data.user, data.token);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setOtpToken(null);
    setOtp('');
    setError('');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-base)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Background grid */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
        opacity: 0.6,
      }} />
      {/* Glow */}
      <div style={{
        position: 'absolute',
        width: 500, height: 500,
        background: 'radial-gradient(circle, rgba(0,212,255,0.06) 0%, transparent 70%)',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
      }} />

      <div style={{
        position: 'relative', zIndex: 1,
        width: '100%', maxWidth: 400,
        padding: '0 20px',
        animation: 'fadeIn 0.4s ease',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{
            display: 'inline-block',
            background: 'white',
            borderRadius: 12,
            padding: '14px 24px',
            marginBottom: 12,
            boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
          }}>
            <img
              src="/smartswitch.png"
              alt="SmartSwitch"
              style={{ height: 72, objectFit: 'contain', display: 'block' }}
            />
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            eGateway Management
          </div>
        </div>

        <div className="card" style={{ padding: 28 }}>

          {!otpToken ? (
            // ── Step 1: credentials ──────────────────────────────────────────
            <>
              <div style={{ marginBottom: 20, fontWeight: 700, fontSize: 15 }}>Sign In</div>
              {error && <div className="alert alert-error" style={{ marginBottom: 14 }}>{error}</div>}
              <form onSubmit={handleLogin}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div className="form-group">
                    <label className="form-label">User ID</label>
                    <input
                      className="form-input"
                      value={userid}
                      onChange={e => setUserid(e.target.value)}
                      autoFocus
                      autoComplete="username"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Password</label>
                    <input
                      className="form-input"
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ width: '100%', justifyContent: 'center', marginTop: 4, padding: '10px' }}
                    disabled={loading}
                  >
                    {loading ? 'Checking…' : 'Continue'}
                  </button>
                </div>
              </form>
            </>
          ) : (
            // ── Step 2: OTP entry ────────────────────────────────────────────
            <>
              <div style={{ marginBottom: 6, fontWeight: 700, fontSize: 15 }}>Verification</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
                A one-time PIN has been sent to your Telegram. Enter it below to complete sign in.
              </div>
              {error && <div className="alert alert-error" style={{ marginBottom: 14 }}>{error}</div>}
              <form onSubmit={handleVerify}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div className="form-group">
                    <label className="form-label">One-Time PIN</label>
                    <input
                      className="form-input"
                      value={otp}
                      onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="6-digit PIN"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoFocus
                      required
                      style={{ letterSpacing: '0.3em', fontSize: 20, textAlign: 'center' }}
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ width: '100%', justifyContent: 'center', padding: '10px' }}
                    disabled={loading || otp.length !== 6}
                  >
                    {loading ? 'Verifying…' : 'Verify & Sign In'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    style={{ width: '100%', justifyContent: 'center', fontSize: 13 }}
                    onClick={handleBack}
                  >
                    ← Back to login
                  </button>
                </div>
              </form>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
